"use client";

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { GraduationCap, Trophy, Target, Rocket, ArrowRight } from 'lucide-react';
export default function HowItWorks() {
  const $ = _c(31);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      threshold: 0.2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    ref,
    isVisible
  } = useScrollAnimation(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      icon: GraduationCap,
      number: "01",
      title: "Learn New Skills",
      description: "Choose from 1000+ courses - both free and premium. Watch video lessons, attend live sessions, and master in-demand skills.",
      color: "blue"
    }, {
      icon: Trophy,
      number: "02",
      title: "Compete & Rank",
      description: "Take assessments, earn scores, and climb the leaderboards. Prove your expertise and stand out from the crowd.",
      color: "purple"
    }, {
      icon: Target,
      number: "03",
      title: "Get Auto-Matched",
      description: "Our AI matches your verified skills with real opportunities from 500+ partner companies. No more endless job hunting!",
      color: "green"
    }, {
      icon: Rocket,
      number: "04",
      title: "Start Working",
      description: "Begin your internship or project. Receive feedback, build your portfolio, and launch your professional career.",
      color: "orange"
    }];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const steps = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      blue: {
        bg: "bg-blue-500",
        gradient: "from-blue-500 to-cyan-500",
        light: "bg-blue-50",
        text: "text-blue-600"
      },
      purple: {
        bg: "bg-purple-500",
        gradient: "from-purple-500 to-pink-500",
        light: "bg-purple-50",
        text: "text-purple-600"
      },
      green: {
        bg: "bg-green-500",
        gradient: "from-green-500 to-emerald-500",
        light: "bg-green-50",
        text: "text-green-600"
      },
      orange: {
        bg: "bg-orange-500",
        gradient: "from-orange-500 to-red-500",
        light: "bg-orange-50",
        text: "text-orange-600"
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const colorClasses = t2;
  const t3 = ref as React.RefObject<HTMLElement>;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 0,
      y: 20
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== isVisible) {
    t5 = isVisible ? {
      opacity: 1,
      y: 0
    } : {};
    $[4] = isVisible;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      duration: 0.6
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-semibold mb-4"><Rocket className="w-4 h-4" /><span>Simple 4-Step Process</span></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">From Learning to<span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Earning in 4 Steps</span></h2>;
    t9 = <p className="text-xl text-gray-600 max-w-3xl mx-auto">Our proven process takes you from complete beginner to industry-ready professional</p>;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t5) {
    t10 = <motion.div initial={t4} animate={t5} transition={t6} className="text-center mb-16">{t7}{t8}{t9}</motion.div>;
    $[10] = t5;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-purple-200 via-green-200 to-orange-200" />;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== isVisible) {
    t12 = <div className="relative">{t11}<div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">{steps.map((step, index) => {
          const colors = colorClasses[step.color as keyof typeof colorClasses];
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} animate={isVisible ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: index * 0.2
          }} className="relative"><div className="relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full"><div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">{step.number}</div><div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center mb-4 shadow-lg`}><step.icon className="w-8 h-8 text-white" /></div><h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3><p className="text-gray-600 leading-relaxed">{step.description}</p>{index < steps.length - 1 && <div className="hidden lg:block absolute -right-8 top-20 text-gray-300"><ArrowRight className="w-6 h-6" /></div>}</div></motion.div>;
        })}</div></div>;
    $[13] = isVisible;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      opacity: 0,
      y: 20
    };
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== isVisible) {
    t14 = isVisible ? {
      opacity: 1,
      y: 0
    } : {};
    $[16] = isVisible;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      duration: 0.6,
      delay: 0.8
    };
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="bg-white rounded-2xl p-6 text-center shadow-lg"><div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">Average 3 Months</div><div className="text-gray-600">From Start to First Opportunity</div></div>;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="bg-white rounded-2xl p-6 text-center shadow-lg"><div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">85% Match Rate</div><div className="text-gray-600">Students Matched to Opportunities</div></div>;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="bg-white rounded-2xl p-6 text-center shadow-lg"><div className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">4.8/5 Rating</div><div className="text-gray-600">Average Student Satisfaction</div></div>;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] !== t14) {
    t19 = <motion.div initial={t13} animate={t14} transition={t15} className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">{t16}{t17}{t18}</motion.div>;
    $[22] = t14;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] !== t10 || $[25] !== t12 || $[26] !== t19) {
    t20 = <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{t10}{t12}{t19}</div>;
    $[24] = t10;
    $[25] = t12;
    $[26] = t19;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t20 || $[29] !== t3) {
    t21 = <section id="how-it-works" className="py-20 md:py-32 bg-gradient-to-br from-gray-50 to-blue-50" ref={t3}>{t20}</section>;
    $[28] = t20;
    $[29] = t3;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  return t21;
}